// Kinetic SVG Energy Ecosystem: Solar -> Hybrid Inverter -> Battery/ESS -> Loads -> Grid
import { useState } from "react";
import { Sun, Cpu, BatteryCharging, Home, Zap } from "lucide-react";

const MODES = {
  day: { label: "Peak Sunlight", active: ["s-i", "i-b", "i-l", "i-g"] },
  night: { label: "Night Discharge", active: ["b-i", "i-l"] },
  cloudy: { label: "Grid Support", active: ["g-i", "i-l", "s-i"] },
};

const Node = ({ x, y, icon: Icon, label, color }) => (
  <g>
    <circle cx={x} cy={y} r="30" fill="#0B1522" stroke={color} strokeWidth="1.5" />
    <circle cx={x} cy={y} r="30" fill="none" stroke={color} strokeWidth="1.5" opacity="0.35">
      <animate attributeName="r" values="30;40;30" dur="2.6s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.35;0;0.35" dur="2.6s" repeatCount="indefinite" />
    </circle>
    <foreignObject x={x - 12} y={y - 12} width="24" height="24">
      <div style={{ color }}><Icon size={24} /></div>
    </foreignObject>
    <text x={x} y={y + 48} textAnchor="middle" fill="#94A3B8" fontSize="11" fontFamily="'JetBrains Mono', monospace">{label}</text>
  </g>
);

const Path = ({ d, on, color = "#10B981" }) => (
  <path d={d} fill="none" stroke={on ? color : "#1E3048"} strokeWidth={on ? 2.5 : 1.5}
    className={on ? "flow-dash" : ""} opacity={on ? 1 : 0.5} />
);

export default function EnergyDiagram() {
  const [mode, setMode] = useState("day");
  const on = (id) => MODES[mode].active.includes(id);
  return (
    <div className="w-full">
      <div className="flex gap-2 mb-4 flex-wrap">
        {Object.entries(MODES).map(([k, v]) => (
          <button key={k} onClick={() => setMode(k)}
            data-testid={`energy-mode-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider transition-all ${
              mode === k ? "bg-emerald-500 text-[#04261C]" : "bg-[#0B1522] text-slate-400 border border-white/10 hover:border-emerald-400/50"}`}>
            {v.label}
          </button>
        ))}
      </div>
      <svg viewBox="0 0 420 400" className="w-full h-auto" role="img" aria-label="Energy ecosystem diagram">
        <Path d="M 90 80 C 200 80, 210 190, 210 190" on={on("s-i")} color="#FBBF24" />
        <Path d="M 210 220 C 210 300, 110 300, 90 320" on={on("i-b")} />
        <Path d="M 90 320 C 110 300, 210 300, 210 220" on={on("b-i")} color="#34D399" />
        <Path d="M 240 200 C 320 200, 330 300, 330 320" on={on("i-l")} />
        <Path d="M 330 80 C 260 100, 240 160, 240 185" on={on("i-g") || on("g-i")} color="#38BDF8" />
        <Node x={90} y={80} icon={Sun} label="Solar" color="#FBBF24" />
        <Node x={210} y={200} icon={Cpu} label="Hybrid Inverter" color="#34D399" />
        <Node x={90} y={320} icon={BatteryCharging} label="LFP Battery" color="#10B981" />
        <Node x={330} y={320} icon={Home} label="Loads" color="#E2E8F0" />
        <Node x={330} y={80} icon={Zap} label="Grid" color="#38BDF8" />
      </svg>
    </div>
  );
}
